import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import AppHeader from "@/layouts/AppHeader";
import WhatsAppFab from "@/components/WhatsAppFab";
import { AppFooter } from "@/layouts/AppFooter";
import { Helmet } from "react-helmet-async";
import { useToast } from "@/hooks/use-toast";
import { useRegion } from "@/context/RegionContext";

// Define TypeScript interfaces for the API response
interface PageData {
  id: number;
  title: string;
  slug: string;
  short_description: string;
  content: string;
  image_path: string | null;
  created_at: string;
  updated_at: string;
}

const CMSPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>(); // Get the slug from the URL
  const [pageData, setPageData] = useState<PageData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { region } = useRegion();

  useEffect(() => {
    // Handle corporate-car-leasing as special case
    if (slug === 'corporate-car-leasing') {
      // Redirect to the static HTML page
      window.location.href = '/malaysia-corporate-car-leasing/index.html';
      return;
    }

    const fetchPageData = async () => {
      try {
        const response = await fetch(
          `${process.env.API_URL}cms/${slug}`
        );
        
        if (!response.ok) {
          // Handle 404 and other error responses
          showErrorAndRedirect("Page Not Found", "The requested page could not be found.");
          return;
        }
        
        const data = await response.json();
        setPageData(data.data); // Set the page data
      } catch (err: any) {
        // Handle network errors and other exceptions
        showErrorAndRedirect("Error", "Opps, so sorry the page you are looking for is not found. Redirecting to homepage.");
        return;
      } finally {
        setLoading(false);
      }
    };

    // Helper function to show toast and redirect
    const showErrorAndRedirect = (title: string, description: string) => {
      toast({
        title,
        description,
        variant: "destructive",
        position: "top",
      });
      
      // Redirect to homepage with replace: true (similar to 301 redirect behavior)
      navigate("/", { replace: true });
    };

    fetchPageData();
  }, [slug, navigate, toast]);

  if (loading) return <div className="text-center mt-10">Loading...</div>;

  return (
    <>
    <WhatsAppFab/>
    <Helmet>
        <title>Europcar {region.countryName}: {pageData?.title}</title>
        <meta name="description" content={pageData?.short_description} />
        <meta name="keywords" content={region.seoKeywords} data-hid="keywords"/>
    </Helmet>
      <AppHeader
        className="bg-neutral-100 pb-4 px-4 md:px-32 relative w-full z-30"
        textColor="text-black"
        borderColor="border-black"
  
      />

    <div className="prose mx-auto mt-10">
      <h1 className="text-3xl font-bold">{pageData?.title}</h1>
      <p className="text-gray-500">{pageData?.short_description}</p>
      <div
        className="prose"
        dangerouslySetInnerHTML={{ __html: pageData?.content || "" }}
      ></div>
    </div>
    <AppFooter/>
    </>
  );
};

export default CMSPage;